<template>
  <div
    class="carousel"
    :class="[$style['carousel'], { [$style['carousel--' + size]]: size }]">
    <div :class="$style['carousel__header']">
      <b v-if="title" :class="$style['carousel__header-title']">
        {{ title }}
      </b>
      <div
        :class="[$style['carousel__navigation'], {[$style['carousel__navigation--show']]: showNavigation}]">
        <Btn
          v-if="showNavigation"
          aria-label="Scroll left"
          :disabled="prevDisabled"
          icon-only="System/arrow-left-s-line"
          border
          shape="round"
          size="small"
          color="ghost"
          @click="goPrev" />
        <Btn
          v-if="showNavigation"
          aria-label="Scroll right"
          :disabled="nextDisabled"
          icon-only="System/arrow-right-s-line"
          shape="round"
          size="small"
          color="ghost"
          border
          @click="goNext" />
      </div>
    </div>

    <div
      ref="container"
      :class="$style['carousel__container-wrapper']">
      <transition-group
        :name="animate ? 'fade-new' : ''"
        tag="div"
        :class="$style['carousel__container']">
        <div
          v-for="(item, index) in items"
          :key="item.cId ?? item.id"
          ref="itemsEl"
          :class="
            [$style['carousel__item'],
             { [$style['carousel__item--active']]: parseInt(index) == active }
            ]">
          <slot
            name="item"
            :item="item" />
          <btn
            v-if="showIcon"
            icon-only="System/zoom-in-line"
            shape="round"
            size="small"
            color="ghost"
            :class="$style['carousel__item__icon']" />
        </div>
      </transition-group>
      <div v-if="!nextDisabled" :class="$style['carousel__mobile-gradient']" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { debounce } from '~/helpers/debounce'
const props = defineProps({
  items: {
    type: [Array, Object],
    default: null
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value: string) => ['small', 'medium-small', 'medium'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  active: {
    type: Number,
    default: null
  },
  showIcon: {
    type: Boolean,
    default: false
  },
  animate: {
    type: Boolean,
    default: false
  }
})

const nextDisabled = ref(false)
const prevDisabled = ref(false)
const showNavigation = ref(false)
const itemWidth = ref(null)
const itemsEl = ref(null)
const container = ref(null)

const toggleNavigationState = timeout => {
  if (!container.value) { return }
  showNavigation.value =
    container.value.scrollWidth > container.value.offsetWidth

  setTimeout(() => {
    if (!container.value) { return }

    showNavigation.value = container.value.scrollWidth > container.value.offsetWidth
    prevDisabled.value = container.value.scrollLeft < 10
    nextDisabled.value = container.value.scrollLeft + container.value.offsetWidth >= container.value.scrollWidth
  }, timeout)
}
const goNext = () => {
  if (!container.value) { return }
  container.value.scrollBy({
    left: itemWidth.value,
    top: 0,
    behavior: 'smooth'
  })
  toggleNavigationState(100)
}
const goPrev = () => {
  if (!container.value) { return }
  container.value.scrollBy({
    left: -itemWidth.value,
    top: 0,
    behavior: 'smooth'
  })
  toggleNavigationState(100)
}

watch(() => props.active, async () => {
  if (!props.active) { return }
  await nextTick()
})

onActivated(async () => {
  await nextTick()
  toggleNavigationState(10)
})

onMounted(async () => {
  if (itemsEl.value) {
    itemWidth.value =
      itemsEl.value[0].offsetWidth +
          parseInt(getComputedStyle(itemsEl.value[0]).marginRight)
  }

  await nextTick()
  toggleNavigationState(10)

  if (!container.value) { return }
  container.value.addEventListener('scroll', debounce(() => {
    toggleNavigationState(0)
  })
  )
})

</script>
<style lang="scss" module>
.carousel {
  position: relative;

  &__header {
    align-items: center;
    font-size: $font-size-m;
    justify-content: space-between;
    display: flex;
  }

  &__header-title {
    margin-bottom: $spacing;
  }

  &__navigation {
    opacity: 0;
    user-select: none;
    transition: opacity $transition-time-slow $transition-easing;

    &--show {
      opacity: 1;
    }

    & > * {
      margin-left: $spacing;
    }

    // cannot be written together with 'and', this will cause it to not work
    @media (pointer: coarse)  {
      display: none;
    }

    @media (hover: none) {
      display: none;
    }
  }

  &__mobile-gradient {
    display: none;
    background: linear-gradient(
      to right,
      rgb(255 255 255 / 0%),
      rgb(255 255 255 / 100%) 90%
    );
    height: 100%;
    position: absolute;
    right: 0;
    top: 0;
    width: 20%;
    z-index: 1;

    @media (pointer: coarse)  {
      display: inline-block;
    }

    @media (hover: none) {
      display: inline-block;
    }
  }

  &__container-wrapper {
    @include scrollbar;
    /* stylelint-disable no-duplicate-selectors */

    & {
      scroll-snap-type: x mandatory;
      scroll-snap-align: start;
      overflow-x: auto;
      overflow-y: hidden;
      width: 100%;
    }

    &::-webkit-scrollbar {
      display: none; /* for Chrome, Safari, and Opera */
    }
  }

  &:hover .carousel__container-wrapper {
    color: $grey;
  }

  &__container {
    flex-direction: row;
    display: flex;
    transition: transform $transition-time-slower $transition-easing;
    padding-top: $spacing;
    padding-bottom: $spacing;
  }

  &__item {
    scroll-snap-align: start;
    margin-right: $spacing * 3;
    width: 40%;
    min-width: 40%;
    max-width: 40%;
    position: relative;

    &__icon {
      color: $black;
      pointer-events: none;
      position: absolute;
      top: $spacing;
      right: $spacing;
      z-index: 2;
    }

    &:last-child {
      margin-right: 0;
    }

    &--active {
      order: -1;
    }
  }

  &--small {
    .carousel__item {
      width: 25%;
      min-width: 120px;
      margin-right: $spacing;
    }
  }

  &--medium-small {
    .carousel__item {
      min-width: 25%;
      width: 25%;

      @media (max-width: #{$breakpoint-mobile}) {
        min-width: 40%;
      }
    }
  }
}
</style>
